import { ScheduleService } from '../../../services/schedule.service';
import { MessageService, ConfirmationService } from 'primeng/api';
import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { SearchCriteria } from '../../../models/SearchCriteria';
import { UserAvailability } from '../../../models/UserAvailability';
import { SchedulerEvent } from '../../../models/SchedulerEvent';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { AvailabilityDialogComponent } from './availability-dialog/availability-dialog.component';
import { AvailabilityBulkDialogComponent } from './availability-bulk-dialog/availability-bulk-dialog.component';
import {
  PresenceBookingService,
  PresenceBookingContext
} from '../../../services/presence-booking.service';
import { DialogService } from 'primeng/dynamicdialog';
import { Calendar, CalendarOptions, EventClickArg } from '@fullcalendar/core';
import {
  FullCalendarComponent,
  FullCalendarModule
} from '@fullcalendar/angular';
import { User } from '../../../models/User';
import { UserAvailabilityService } from '../../../services/user-availability.service';
import { UserService } from '../../../services/user.service';
import { AuthService } from '../../../services/auth.service';
import { environment } from '../../../../environments/environment';
import interactionPlugin from '@fullcalendar/interaction';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/resource-timegrid';
import { UtilsService } from '../../../services/utils.service';
import { Select } from 'primeng/select';
import { VisibleOnlyDirective } from '../../../directives/visible-only.directive';
import { FormsModule } from '@angular/forms';
import { ButtonDirective } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';

@Component({
  selector: 'app-user-availability',
  templateUrl: './user-availability.component.html',
  styleUrls: ['./user-availability.component.scss'],

  imports: [
    Select,
    VisibleOnlyDirective,
    FormsModule,
    FullCalendarModule,
    TranslatePipe,
    ButtonDirective,
    Tooltip
  ]
})
export class UserAvailabilityComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() user: User | null = null;
  public presenceBooking: PresenceBookingContext | null = null;
  public isMonthView = false;
  private returningToSchedule = false;
  private msgEventDeleteConfirm = '';
  private msgEventSaveSuccess = '';
  private msgEventDeleteSuccess = '';
  private msgNoEvents = '';
  private calendarLicense = environment.fullCalendarLicenseKey;
  private scheduleStorageKey = environment.scheduleStorageKey;
  public searchCriteria: SearchCriteria;
  public calendarApi: Calendar | null = null;
  public profileAvailability: Array<UserAvailability> | null = null;
  public events: Array<SchedulerEvent> | null = null;
  public loading = true;
  public users: Array<User> | null = null;
  public userResource: any;
  public userResources: Array<any> | null = null;
  public selectedUser: any;
  public viewSelections: Array<any> | null = null;
  private userId: any;

  public calendarOptions: CalendarOptions = {
    plugins: [
      interactionPlugin,
      resourceTimeGridPlugin,
      dayGridPlugin,
      timeGridPlugin
    ],
    schedulerLicenseKey: this.calendarLicense,
    timeZone: 'local',
    initialDate: new Date(),
    initialView: 'timeGridWeek',
    editable: true,
    selectable: true,
    slotMinTime: '06:00:00',
    slotMaxTime: '24:00:00',
    slotDuration: '00:30:00',
    resourceOrder: 'sortOrder',
    resources: [],
    customButtons: {
      dayGridMonthCustom: {
        text: 'month',
        click: () => {
          this.setDate('month');
        }
      },
      timeGridWeekCustom: {
        text: 'week',
        click: () => {
          this.setDate('week');
        }
      },
      timeGridDayCustom: {
        text: 'day',
        click: () => {
          this.setDate('day');
        }
      },
      backCustom: {
        text: '<',
        click: () => {
          this.setDate('prev');
        }
      },
      todayCustom: {
        text: 'today',
        click: () => {
          this.setDate('today');
        }
      },
      nextCustom: {
        text: '>',
        click: () => {
          this.setDate('next');
        }
      }
    },
    // headerToolbar: false,
    headerToolbar: {
      left: 'dayGridMonthCustom,timeGridWeekCustom,timeGridDayCustom',
      center: 'title',
      right: 'backCustom,todayCustom,nextCustom'
    },
    firstDay: 1,
    allDaySlot: false,
    select: this.handleEventSelect.bind(this),
    eventClick: this.handleDateClick.bind(this),
    eventChange: this.handleEventChange.bind(this),
    eventsSet: this.handleEvents.bind(this)
  };

  @ViewChild('availabilityProfile') calendarComponent:
    | FullCalendarComponent
    | undefined;

  private setDate(direction: string): void {
    switch (direction) {
      case 'month':
        this.calendarApi?.changeView('dayGridMonth');
        // Keep the searched date in sync with the currently viewed date so
        // the view type and date stay consistent when switching Month/Week/Day.
        this.searchCriteria.startDate =
          this.calendarApi?.getDate() || new Date();
        break;
      case 'week':
        this.calendarApi?.changeView('timeGridWeek');
        this.searchCriteria.startDate =
          this.calendarApi?.getDate() || new Date();
        break;
      case 'day':
        this.calendarApi?.changeView('timeGridDay');
        this.searchCriteria.startDate =
          this.calendarApi?.getDate() || new Date();
        break;
      case 'prev':
        this.calendarApi?.prev();
        this.searchCriteria.startDate =
          this.calendarApi?.getDate() || new Date();
        break;
      case 'today':
        this.calendarApi?.today();
        this.searchCriteria.startDate =
          this.calendarApi?.getDate() || new Date();
        break;
      case 'next':
        this.calendarApi?.next();
        this.searchCriteria.startDate =
          this.calendarApi?.getDate() || new Date();
        break;
    }
    this.getEvents();
  }

  constructor(
    private userAvailabilityService: UserAvailabilityService,
    private dialogService: DialogService,
    private translateService: TranslateService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private scheduleService: ScheduleService,
    private userService: UserService,
    private authService: AuthService,
    private utilsService: UtilsService,
    private router: Router,
    private presenceBookingService: PresenceBookingService
  ) {
    this.searchCriteria = {} as SearchCriteria;
    this.searchCriteria.startDate = new Date();
    this.userResource = {
      id: 'USERS',
      title: 'event.resources.user-selector-title',
      filterTitle: 'event.resources.user-filter-title'
    };
  }

  ngOnInit(): void {
    this.calendarOptions.locale =
      this.translateService.getCurrentLang() ||
      this.translateService.getFallbackLang() ||
      'fi';
    this.userId = this.user?.id;
    this.loadTranslations();

    // Show the "back to Ajanvaraus" quick-link when arrived from the schedule.
    this.presenceBooking = this.presenceBookingService.getContext();

    // Localize the Month/Week/Day view buttons.
    const cb = this.calendarOptions.customButtons;
    if (cb) {
      if (cb.dayGridMonthCustom) {
        cb.dayGridMonthCustom.text =
          this.translateService.instant('availability.month');
      }
      if (cb.timeGridWeekCustom) {
        cb.timeGridWeekCustom.text =
          this.translateService.instant('availability.week');
      }
      if (cb.timeGridDayCustom) {
        cb.timeGridDayCustom.text =
          this.translateService.instant('availability.day');
      }
      if (cb.todayCustom) {
        cb.todayCustom.text =
          this.translateService.instant('availability.today');
      }
    }

    const organizationUnit = this.user?.activeOrganizationUnit;
    if (organizationUnit) {
      if (organizationUnit.scheduleEventDuration) {
        this.calendarOptions.slotDuration =
          organizationUnit.scheduleEventDuration;
      }
      if (organizationUnit.scheduleStart) {
        this.calendarOptions.slotMinTime = organizationUnit.scheduleStart;
      }
      if (organizationUnit.scheduleEnd) {
        this.calendarOptions.slotMaxTime = organizationUnit.scheduleEnd;
      }
    }

    this.userService.getScheduleUsers().subscribe((result) => {
      this.users = result;
      this.userResources = this.users!.map((user) => {
        return {
          id: user.id,
          title: `${user.lastName} ${user.firstName}`,
          sortOrder: `${user.lastName}`
        };
      });

      // Set the default selected user
      this.selectedUser = this.userResources.find(
        (user) => user.id === this.userId
      );
    });
  }

  resourceFilterChange(event: any, type: string): void {
    if (this.authService.isPermission('update_all_users_availability')) {
      this.userId = this.selectedUser.id;
      this.getEvents();
    }
  }

  ngAfterViewInit(): void {
    if (!this.calendarApi && this.calendarComponent) {
      this.calendarApi = this.calendarComponent!.getApi();
    }
    // calendarApi is available here so request events using the calendar view range
    this.getEvents();
  }

  refreshCalendar(): void {
    if (!this.calendarApi && this.calendarComponent) {
      this.calendarApi = this.calendarComponent.getApi();
    }
    this.calendarApi?.setOption('contentHeight', 650);
    this.calendarApi?.changeView('timeGridWeek');
    // The calendar may have been initialised while its tab/section was hidden
    // (0 size). Force a re-layout now that it is visible.
    this.calendarApi?.updateSize();
    this.getEvents();
  }

  public getEvents(): void {
    this.profileAvailability = [];
    this.events = [];
    this.calendarApi?.removeAllEvents();

    if (this.user) {
      const pastDate = new Date();
      pastDate.setMonth(pastDate.getMonth() - 2);

      const futureDate = new Date();
      futureDate.setMonth(futureDate.getMonth() + 12);

      const userAvailability = {
        userId: this.userId,
        startDate: this.calendarApi?.view.currentStart,
        endDate: this.calendarApi?.view.currentEnd
      };

      this.userAvailabilityService
        .getUserAvailabilityByDates(userAvailability)
        .subscribe(
          (res) => {
            if (res.length === 0) {
              this.calendarOptions.events = [];
              this.loading = false;
            } else {
              this.profileAvailability = res;
              this.mapAvailabilityToCalendarEvents(res);
            }
          },
          (error) => {
            this.calendarOptions.events = [];
            this.loading = false;
          }
        );
    }
  }

  /**
   * Load all translations
   */
  private loadTranslations(): void {
    this.translateService
      .get([
        'event.messages.event-remove-confirmation',
        'event.messages.event-save-success',
        'event.messages.event-delete-success',
        'event.messages.no-events-found',
        'event.btn-today',
        'event.btn-this-week',
        'event.btn-this-month'
      ])
      .subscribe((result) => {
        this.msgEventDeleteConfirm =
          result['event.messages.event-remove-confirmation'];
        this.msgEventSaveSuccess = result['event.messages.event-save-success'];
        this.msgEventDeleteSuccess =
          result['event.messages.event-delete-success'];
        this.msgNoEvents = result['event.messages.no-events-found'];
        this.viewSelections = [
          {
            name: result['event.btn-today'],
            code: 0
          },
          {
            name: result['event.btn-this-week'],
            code: 1
          },
          {
            name: result['event.btn-this-month'],
            code: 2
          }
        ];
      });
  }

  private mapAvailabilityToCalendarEvents(
    profileAvailability: Array<UserAvailability>
  ): void {
    const events = profileAvailability.map((event) => {
      let eventBg = '#C7B3D9';
      if (event.type) {
        eventBg = this.scheduleService.getEventColor(event.type);
      }
      return {
        id: event.id,
        start: event.availabilityStart,
        end: event.availabilityEnd,
        title: event.description,
        event,
        backgroundColor: eventBg,
        borderColor: eventBg
      };
    });

    events?.forEach((e) => {
      this.calendarApi!.getCurrentData().calendarApi.addEvent(e);
    });
    this.loading = false;
  }

  private availabilityToEvent(availability: UserAvailability): void {
    // Changes object from UserAvailability to SchedulerEvent and adds it to the calendarApi
    let eventBg = '#C7B3D9';
    if (availability.type) {
      eventBg = this.scheduleService.getEventColor(availability.type);
    }
    const event = {
      title: availability.description,
      start: availability.availabilityStart,
      end: availability.availabilityEnd,
      event: availability,
      id: availability.id,
      backgroundColor: eventBg,
      borderColor: eventBg
    };
    this.calendarApi!.getCurrentData().calendarApi.addEvent(event);
  }

  public handleEventSelect(info: any): void {
    // Selecting empty calendar space opens the enhanced date-range/breaks dialog,
    // prefilled to the selected day and time. Editing an existing block still
    // uses the single-period dialog (handleDateClick).
    this.openBulkDialog('day', info);
  }

  public handleDateClick(info: EventClickArg): void {
    const profileObject = info.event.extendedProps.event;
    const data = {
      info,
      profileObject,
      userAvailability: {
        id: profileObject.id,
        userId: this.userId,
        type: profileObject.type || '',
        description: profileObject.description || '',
        availabilityStart: profileObject.availabilityStart,
        availabilityEnd: profileObject.availabilityEnd
      }
    };

    this.handleDialog(data);
  }

  public handleDialog(data: any): void {
    const ref = this.dialogService.open(AvailabilityDialogComponent, {
      header: this.translateService.instant('availability.edit-presence'),
      data,
      width: '520px',
      closable: true,
      draggable: true,
      resizable: true,
      baseZIndex: 10000
    });

    ref!.onClose.subscribe((retValue: any) => {
      if (retValue) {
        if (retValue.type === 'newAvailability' && retValue.data) {
          this.profileAvailability?.push(retValue.data);
          this.availabilityToEvent(retValue.data);
        } else if (retValue.type === 'updateAvailability') {
          const delIndex = this.profileAvailability?.findIndex(
            (a) => a.id === retValue.data.id
          );
          if (
            (delIndex || delIndex === 0) &&
            delIndex !== -1 &&
            this.profileAvailability
          ) {
            this.profileAvailability.splice(delIndex, 1, retValue.data);
          }
          const updateEvent =
            this.calendarApi!.getCurrentData().calendarApi.getEventById(
              retValue.data.id
            );
          if (updateEvent) {
            updateEvent.remove();
            this.availabilityToEvent(retValue.data);
          }
        } else if (
          retValue.type === 'delAvailability' &&
          retValue.data &&
          retValue.data.id
        ) {
          const delIndex = this.profileAvailability?.findIndex(
            (a) => a.id === retValue.data.id
          );
          if (
            (delIndex || delIndex === 0) &&
            delIndex !== -1 &&
            this.profileAvailability
          ) {
            this.profileAvailability.splice(delIndex, 1);
          }
          const delEvent =
            this.calendarApi!.getCurrentData().calendarApi.getEventById(
              retValue.data.id
            );
          if (delEvent) {
            delEvent.remove();
          }
        }
      }
    });
  }

  /**
   * Open the enhanced "add presence" dialog.
   *  - 'range' (toolbar button): prefill the currently visible week/day range,
   *    Mon-Fri, using the org-unit slot times.
   *  - 'day' (calendar drag-select): prefill a single day + the selected times.
   */
  public openBulkDialog(mode: 'range' | 'day', info?: any): void {
    const pad = (n: number): string => (n < 10 ? `0${n}` : `${n}`);
    const hhmm = (d: Date): string =>
      `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    const data: any = { userId: this.userId };

    if (mode === 'day' && info?.start) {
      const start: Date = info.start;
      const end: Date | null = info.end || null;
      data.defaultStartDate = start;
      data.defaultEndDate = start;
      data.onlyJsDay = start.getDay();
      data.defaultStartTime = hhmm(start);
      if (end && end > start && end.getDate() === start.getDate()) {
        data.defaultEndTime = hhmm(end);
      }
    } else {
      const viewStart = this.calendarApi?.view.currentStart || new Date();
      const viewEnd = this.calendarApi?.view.currentEnd; // exclusive
      data.defaultStartDate = viewStart;
      if (viewEnd) {
        const inclusiveEnd = new Date(viewEnd);
        inclusiveEnd.setDate(inclusiveEnd.getDate() - 1);
        data.defaultEndDate = inclusiveEnd;
      } else {
        data.defaultEndDate = viewStart;
      }
      data.defaultStartTime =
        this.toHhmm(this.calendarOptions.slotMinTime) || '08:00';
      data.defaultEndTime =
        this.toHhmm(this.calendarOptions.slotMaxTime) || '16:00';
    }

    const ref = this.dialogService.open(AvailabilityBulkDialogComponent, {
      header: this.translateService.instant('availability.add-presence'),
      data,
      width: '640px',
      closable: true,
      draggable: true,
      resizable: true,
      baseZIndex: 10000
    });

    ref!.onClose.subscribe((retValue: any) => {
      if (retValue && retValue.type === 'batchAdded') {
        const msg = this.translateService.instant('availability.save-summary', {
          added: retValue.added,
          replaced: retValue.replaced
        });
        this.messageService.add({
          severity: 'success',
          summary: '',
          detail: msg
        });
        this.getEvents();
      }
    });
  }

  /**
   * Return to the Ajanvaraus (schedule) module. The presence context is left in
   * place so the schedule can restore the exact view/date it left; the schedule
   * consumes and clears it on init.
   */
  public returnToSchedule(): void {
    if (this.presenceBooking?.returnUrl) {
      this.returningToSchedule = true;
      this.router.navigateByUrl(this.presenceBooking.returnUrl);
    }
  }

  ngOnDestroy(): void {
    // If the user leaves the availability view by any route other than the
    // back link, drop the stale round-trip context.
    if (!this.returningToSchedule) {
      this.presenceBookingService.clearContext();
    }
  }

  private toHhmm(value: any): string | null {
    if (!value || typeof value !== 'string') {
      return null;
    }
    const parts = value.split(':');
    if (parts.length < 2) {
      return null;
    }
    return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
  }

  public handleEventChange(info: any): void {
    const profileObject = info.event.extendedProps.event;

    const updatedProfile: UserAvailability = {
      ...profileObject,
      availabilityStart: info.event.start,
      availabilityEnd: info.event.end
    };

    this.userAvailabilityService
      .saveUserAvailability(updatedProfile)
      .subscribe((res) => {});
  }

  public copyWeek(): void {
    this.messageService.clear();

    const cMessage = this.translateService.instant(`availability.want-to-copy`);
    const cHeader = this.translateService.instant(
      `availability.copy-confirmation`
    );

    this.confirmationService.confirm({
      message: cMessage,
      header: cHeader,
      icon: 'pi pi-info-circle',
      accept: () => {
        if (this.user && this.calendarApi) {
          const info = {
            userId: this.userId,
            startDate: this.calendarApi.view.activeStart,
            endDate: this.calendarApi.view.activeEnd,
            type: this.calendarApi.view.type,
            until: new Date('2022-10-30')
          };

          if (this.calendarApi.view.type === 'timeGridDay') {
            info.startDate = this.calendarApi.view.currentStart;
            info.endDate = this.calendarApi.view.currentEnd;
          }

          this.userAvailabilityService
            .copyUserAvailability(info)
            .subscribe((res) => {
              const events = this.calendarApi?.getEvents();

              if (events) {
                const sd = info.startDate;
                const ed = info.endDate;

                if (this.calendarApi?.view.type === 'timeGridWeek') {
                  sd.setDate(sd.getDate() + 7);
                  ed.setDate(ed.getDate() + 7);
                } else if (this.calendarApi?.view.type === 'timeGridDay') {
                  sd.setDate(sd.getDate() + 1);
                  ed.setDate(ed.getDate() + 1);
                }
                events.map((e) => {
                  if (
                    e.start &&
                    e.start >= info.startDate &&
                    e.end &&
                    e.end <= info.endDate
                  ) {
                    e.remove();
                  }
                });
              }

              this.mapAvailabilityToCalendarEvents(res);

              const msg = this.translateService.instant(
                'availability.events-copied-successfully'
              );
              this.messageService.add({
                severity: 'success',
                summary: '',
                detail: msg
              });
            });
        }
      }
    });
  }

  private handleEvents(info: any): void {
    // Copy is only meaningful in day/week view, so hide it in month view.
    this.isMonthView = this.calendarApi?.view.type === 'dayGridMonth';
  }
}
